import { safeLocalStorage, useAdminStore } from 'shared-components';

import { clearDatadogUser, setDatadogUser } from '@services/datadog';
import { userStateBridge } from '@services/userStateBridge';
import { v1RepositoryInstance } from '@services/v1Repository';

import type { AdminRemoteApi, ImpersonationStackEntry, RefreshResult } from './remoteApi.types';

const STORAGE_ROLES = 'rx-roles';
const STORAGE_TOKEN = 'rx-token';
const STORAGE_REFRESHTOKEN = 'rx-refreshtoken';
const STORAGE_IMPERSONATEDUNTIL = 'rx-impersonateduntil';
const STORAGE_IMPERSONATIONSTACK = 'rx-impersonationstack';

const COOKIE_SENTINEL = 'cookie';

const store = (key: string, value: string | null): void => {
  if (value === undefined || value === null) {
    safeLocalStorage.removeItem(key);
  } else {
    safeLocalStorage.setItem(key, value);
  }
};

const createAdminRemoteApi = (): AdminRemoteApi => {
  let stack: ImpersonationStackEntry[] = [];
  let pendingRefresh: Promise<RefreshResult> | undefined;
  let pendingSelfRefresh: Promise<void> | undefined;
  let invalidSessionHandler: (() => Promise<RefreshResult>) | undefined;
  let navigate: ((to: string) => void) | undefined;

  const getToken = (): string | null => safeLocalStorage.getItem(STORAGE_TOKEN);
  const setToken = (value: string | null) => store(STORAGE_TOKEN, value);
  const getRefreshToken = (): string | null => safeLocalStorage.getItem(STORAGE_REFRESHTOKEN);
  const setRefreshToken = (value: string | null) => store(STORAGE_REFRESHTOKEN, value);
  const getImpersonatedUntil = (): string | null => safeLocalStorage.getItem(STORAGE_IMPERSONATEDUNTIL);
  const setImpersonatedUntil = (value: string | null) => store(STORAGE_IMPERSONATEDUNTIL, value);

  const getRoles = (): string[] => {
    const parsed = safeLocalStorage.getJson<unknown>(STORAGE_ROLES, []);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  };

  const setRoles = (value: string[] | null) => {
    store(STORAGE_ROLES, value ? JSON.stringify(value) : null);
    useAdminStore.getState().setRoles(value ?? []);
  };

  const persistStack = () => store(STORAGE_IMPERSONATIONSTACK, stack.length > 0 ? JSON.stringify(stack) : null);

  const restoreStack = () => {
    const parsed = safeLocalStorage.getJson<unknown>(STORAGE_IMPERSONATIONSTACK, []);
    stack = Array.isArray(parsed) ? (parsed as ImpersonationStackEntry[]) : [];
  };

  restoreStack();

  useAdminStore.getState().setRoles(getRoles());

  const clearSession = () => {
    userStateBridge.clearUser();
    clearDatadogUser();
    setToken(null);
    setRefreshToken(null);
    setRoles(null);
    setImpersonatedUntil(null);
    store(STORAGE_IMPERSONATIONSTACK, null);
    stack = [];
  };

  const doRefreshSelf = async (): Promise<void> => {
    if (!getToken()) {
      try {
        const roles = await v1RepositoryInstance.execute({
          method: 'reflectCookie',
        });
        setToken(COOKIE_SENTINEL);
        setRefreshToken(COOKIE_SENTINEL);
        setRoles(roles ?? []);
      } catch (error) {
        userStateBridge.clearUser();
        userStateBridge.setSessionReady(true);
        clearDatadogUser();
        throw error instanceof Error ? error : new Error('No token available');
      }
    }

    const appState = await v1RepositoryInstance.execute({
      method: 'getApplicationState',
      refreshTokenCallback: remoteApi.tryRefreshToken,
    });
    userStateBridge.setUser(appState ?? {});
    userStateBridge.setSessionReady(true);
    useAdminStore.getState().setRoles(getRoles());

    if (appState?.id) {
      setDatadogUser(String(appState.id));
    }
  };

  const performRefreshToken = async (): Promise<RefreshResult> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;

    try {
      const roles = await v1RepositoryInstance.execute({
        method: 'refreshCookie',
      });

      if (roles) {
        setToken(COOKIE_SENTINEL);
        setRefreshToken(COOKIE_SENTINEL);
        setRoles(roles);
      }

      return {
        token: COOKIE_SENTINEL,
        refreshToken: COOKIE_SENTINEL,
        roles: roles ?? [],
      };
    } catch {
      if (invalidSessionHandler) {
        const handled = await invalidSessionHandler();
        if (handled?.token) {
          setToken(handled.token);
          setRefreshToken(handled.refreshToken);
          setRoles(handled.roles);
          return handled;
        }
      }
      return null;
    }
  };

  const remoteApi: AdminRemoteApi = {
    async login(username, password, expectedRoles) {
      try {
        const roles = await v1RepositoryInstance.execute({
          method: 'cookieLogin',
          body: { username, password, expectedRoles },
        });

        setToken(COOKIE_SENTINEL);
        setRefreshToken(COOKIE_SENTINEL);
        setRoles(roles ?? []);

        await remoteApi.refreshSelf();
      } catch (error) {
        setToken(null);
        setRefreshToken(null);
        setRoles(null);
        throw error;
      }
    },

    async refreshSelf() {
      if (pendingSelfRefresh === undefined) {
        pendingSelfRefresh = doRefreshSelf();
        pendingSelfRefresh.then(() => (pendingSelfRefresh = undefined)).catch(() => (pendingSelfRefresh = undefined));
      }
      return pendingSelfRefresh;
    },

    async tryRefreshToken() {
      if (pendingRefresh === undefined) {
        pendingRefresh = performRefreshToken();
        pendingRefresh.then(() => (pendingRefresh = undefined)).catch(() => (pendingRefresh = undefined));
      }
      return pendingRefresh;
    },

    async logout() {
      const impersonating = stack.length > 0;

      if (!impersonating) {
        try {
          await v1RepositoryInstance.execute({ method: 'revokeCookie' });
        } catch {
          // the session ends regardless of the server's answer
        }
      }

      if (impersonating) {
        const previous = stack.pop() as ImpersonationStackEntry;

        setToken(previous.token);
        setRoles(previous.roles);
        setImpersonatedUntil(previous.impersonatedUntil ?? null);
        persistStack();

        try {
          await remoteApi.refreshSelf();
        } catch {
          // stay put if the outer session cannot be restored
        }

        navigate?.(`${previous.route.pathname}${previous.route.search}`);
        userStateBridge.setImpersonated(null);
        return;
      }

      clearSession();
      navigate?.(`/${useAdminStore.getState().user.locale ?? 'de'}/login?noRedirect=true&notice=logout.success`);
    },

    getUserRoles: getRoles,

    validateRoles(required) {
      if (typeof required === 'boolean' || required === null || required === undefined) return true;
      if (required.length === 0) return true;

      const roles = getRoles();
      if (roles.length === 0) return false;

      return required.some((role) => roles.includes(role));
    },

    isAuthenticated: () => userStateBridge.isAuthenticated(),

    setInvalidSessionHandler(handler) {
      invalidSessionHandler = handler;
    },

    setNavigate(next) {
      navigate = next;
    },
  };

  const impersonatedUntil = getImpersonatedUntil();
  if (impersonatedUntil) {
    try {
      const timeLeft = (JSON.parse(impersonatedUntil) - Date.now()) / 1000;
      if (timeLeft > 0) {
        userStateBridge.setImpersonated({ timeout: timeLeft });
      } else {
        void remoteApi.logout();
      }
    } catch {
      setImpersonatedUntil(null);
    }
  }

  return remoteApi;
};

const remoteApi = createAdminRemoteApi();

export { createAdminRemoteApi, remoteApi };
